import isEqual from 'lodash.isequal';
import { isValidPhoneNumber } from 'libphonenumber-js';
import sameElementsArrays from '../../utils/sameElementsArrays';
import fieldsErrors from '../../utils/fieldsErrors';
import isEmailValid from '../../utils/isEmailValid';

const SELECTED_NEXT = { conflicts: true, checked: 'next' };
const SELECTED_PREV = { conflicts: false, checked: 'prev' };
const SELECTED_PREV_CONFLICT = { conflicts: true, checked: 'prev' };
const NULL_INFO = { conflicts: false, checked: null };

export const twitterUsernameHandle = [
  {
    validator: (itm, value) =>
      (value && !value.includes('@')) || !value
        ? Promise.resolve()
        : Promise.reject(new Error(fieldsErrors.withoutAt)),
  },
];

export const latValidationRules = [
  {
    validator: (itm, value) =>
      (!isNaN(value) && parseFloat(value) < 91) || isNaN(value) || !value
        ? Promise.resolve()
        : Promise.reject(new Error(fieldsErrors.max(90))),
  },
  {
    validator: (itm, value) =>
      (!isNaN(value) && parseFloat(value) > -91) || isNaN(value) || !value
        ? Promise.resolve()
        : Promise.reject(new Error(fieldsErrors.min(-90))),
  },
];

export const lngValidationRules = [
  {
    validator: (itm, value) =>
      (!isNaN(value) && parseFloat(value) < 181) || isNaN(value) || !value
        ? Promise.resolve()
        : Promise.reject(new Error(fieldsErrors.max(180))),
  },
  {
    validator: (itm, value) =>
      (!isNaN(value) && parseFloat(value) > -181) || isNaN(value) || !value
        ? Promise.resolve()
        : Promise.reject(new Error(fieldsErrors.min(-180))),
  },
];

export const parseUploadsToPicturesWall = (uploads) =>
  uploads?.map((itm) => ({
    uid: `-${itm.id}`,
    name: itm.title,
    url: itm.file_url,
    status: 'done',
  }));

export function checkOverrides(prev, next) {
  // Deleted Checks
  if (!next?.deleted_at && prev?.deleted_at) return SELECTED_NEXT;
  if (!prev?.deleted_at && next?.deleted_at) return SELECTED_PREV_CONFLICT;

  // Ownership Checks
  if (!prev.is_owned && next?.is_owned) return SELECTED_NEXT;
  if (prev?.is_owned && !next?.is_owned) return SELECTED_PREV_CONFLICT;

  return undefined;
}

const compareStrings = (prev, next) => {
  if (!prev && !next) return NULL_INFO;
  if (!next) return SELECTED_PREV_CONFLICT;
  if (!prev) return SELECTED_NEXT;

  if (prev === next) return SELECTED_PREV;

  return SELECTED_NEXT;
};

const compareBooleans = (prev, next) => {
  if (prev === null && next === null) return NULL_INFO;
  if (next === null) return SELECTED_PREV_CONFLICT;
  if (prev === null) return SELECTED_NEXT;

  if (prev === next) return SELECTED_PREV;

  return SELECTED_NEXT;
};

const compareArrays = (prev, next) => {
  if (!prev.length && !next.length) return NULL_INFO;
  if (!next.length) return SELECTED_PREV_CONFLICT;
  if (!prev.length) return SELECTED_NEXT;

  if (sameElementsArrays(prev, next)) return SELECTED_PREV;

  return SELECTED_NEXT;
};

const compareOptions = (prev, next) => {
  if (!prev?.value && !next?.value) return NULL_INFO;
  if (!next?.value) return SELECTED_PREV_CONFLICT;
  if (!prev?.value) return SELECTED_NEXT;

  if (prev?.value === next?.value) return SELECTED_PREV;

  return SELECTED_NEXT;
};

const compareSurveys = (prev, next) => {
  // Survey Also Checks for Progress Percentage
  if (prev.survey_progress_percentage < next.survey_progress_percentage)
    return SELECTED_NEXT;
  if (prev.survey_progress_percentage > next.survey_progress_percentage)
    return SELECTED_PREV_CONFLICT;

  if (!prev.survey_id && !next.survey_id) return NULL_INFO;
  if (!next.survey_id) return SELECTED_PREV_CONFLICT;
  if (!prev.survey_id) return SELECTED_NEXT;

  if (prev.survey_id === next.survey_id) return SELECTED_PREV;

  return SELECTED_NEXT;
};

export function getRowConfig(type, prev, next) {
  switch (type) {
    case 'tags':
    case 'photos':
    case 'tags_input':
      return compareArrays(prev, next);
    case 'select':
      return compareOptions(prev, next);
    case 'bool':
      return compareBooleans(prev, next);
    case 'survey':
      return compareSurveys(prev, next);
    case 'contacts':
      return NULL_INFO;
    default:
      return compareStrings(prev, next);
  }
}

export const CONTACT_MERGE_FIELDS = [
  'contact_type_id',
  'is_primary',
  'name',
  'email',
  'phone',
];

export const CONTACT_FIELD_LABELS = {
  contact_type_id: 'Type',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  is_primary: 'Primary',
};

const normalizeContactValue = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

export const pickContactFields = (contact) =>
  CONTACT_MERGE_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field]: contact?.[field] ?? null }),
    {},
  );

const isExactContactMatch = (a, b) =>
  isEqual(pickContactFields(a), pickContactFields(b));

const VALID_SCORE = 1;
const INVALID_SCORE = 0.5;

const isValidEmailValue = (value) => {
  const email = normalizeContactValue(value);
  return !!email && !!isEmailValid(email);
};

// react-phone-input-2 stores the full international number with the dial code
// but no leading "+", so restore it before validating as E.164.
const isValidPhoneValue = (value) => {
  const phone = normalizeContactValue(value);
  if (!phone) return false;
  const e164 = phone.startsWith('+') ? phone : `+${phone}`;
  return isValidPhoneNumber(e164);
};

const emailScore = (value) => {
  if (!normalizeContactValue(value)) return 0;
  return isValidEmailValue(value) ? VALID_SCORE : INVALID_SCORE;
};

const phoneScore = (value) => {
  if (!normalizeContactValue(value)) return 0;
  return isValidPhoneValue(value) ? VALID_SCORE : INVALID_SCORE;
};

const fieldScore = (field, value) => {
  if (field === 'email') return emailScore(value);
  if (field === 'phone') return phoneScore(value);
  return normalizeContactValue(value) ? 1 : 0;
};

const contactCompleteness = (contact) =>
  ['name', 'email', 'phone'].reduce(
    (score, field) => score + fieldScore(field, contact?.[field]),
    0,
  );

export const getLessCompleteSide = (prev, next) => {
  if (!prev || !next) return null;

  const prevScore = contactCompleteness(prev);
  const nextScore = contactCompleteness(next);

  if (prevScore === nextScore) return null;
  return prevScore < nextScore ? 'prev' : 'next';
};

export const getContactDefaultSide = (prev, next) => {
  if (!next) return 'prev';
  if (!prev) return 'next';
  // On equal completeness prefer the existing (prev) contact.
  return contactCompleteness(next) > contactCompleteness(prev)
    ? 'next'
    : 'prev';
};

/**
 * Pairs up two contact lists (already filtered by contact type) into ordered
 * merge rows. Match priority:
 *   1. exact     - every merge field is equal
 *   2. id        - same contact id (same contact, some fields differ)
 *   3. email     - same email but different id
 *   4. prevOnly  - contact present only on the prev side
 *   5. nextOnly  - contact present only on the next side
 *
 * @param {Array} prev
 * @param {Array} next
 * @returns {Array<{ key: string, kind: string, prev: ?Object, next: ?Object }>}
 */
export const mergeContacts = (prev, next) => {
  const prevList = (prev || []).filter(Boolean);
  const nextList = (next || []).filter(Boolean);

  const matchedPrev = new Set();
  const matchedNext = new Set();

  const exact = [];
  const byId = [];
  const byEmail = [];

  const pair = (predicate, bucket, kind) => {
    prevList.forEach((p, pi) => {
      if (matchedPrev.has(pi)) return;

      const ni = nextList.findIndex(
        (n, idx) => !matchedNext.has(idx) && predicate(p, n),
      );

      if (ni !== -1) {
        matchedPrev.add(pi);
        matchedNext.add(ni);
        bucket.push({ kind, prev: p, next: nextList[ni] });
      }
    });
  };

  // 1. exact matches
  pair(isExactContactMatch, exact, 'exact');

  // 2. same id (same contact)
  pair((p, n) => p?.id != null && n?.id != null && p.id === n.id, byId, 'id');

  // 3. same email (different contact id)
  pair(
    (p, n) => {
      const pEmail = normalizeContactValue(p?.email).toLowerCase();
      return (
        !!pEmail && pEmail === normalizeContactValue(n?.email).toLowerCase()
      );
    },
    byEmail,
    'email',
  );

  const prevOnly = prevList
    .filter((_, pi) => !matchedPrev.has(pi))
    .map((p) => ({ kind: 'prevOnly', prev: p, next: null }));

  const nextOnly = nextList
    .filter((_, ni) => !matchedNext.has(ni))
    .map((n) => ({ kind: 'nextOnly', prev: null, next: n }));

  return [...exact, ...byId, ...byEmail, ...prevOnly, ...nextOnly].map(
    (row, index) => ({
      ...row,
      key: `${row.kind}-${row.prev?.id ?? 'x'}-${row.next?.id ?? 'x'}-${index}`,
    }),
  );
};
